// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// Primitive component (shadcn/ui, MIT). Themed through NodeControl tokens.

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "flex gap-nc-3 rounded-lg border p-nc-4 text-nc-2 leading-nc-2",
  {
    variants: {
      tone: {
        neutral: "border-border bg-card text-foreground",
        info: "border-nc-info/30 bg-nc-info/5 text-foreground",
        warning: "border-nc-warning/30 bg-nc-warning/5 text-foreground",
        error: "border-nc-error/30 bg-nc-error/5 text-foreground",
      },
    },
    defaultVariants: {
      tone: "neutral",
    },
  },
);

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: ReactNode;
}

export function Alert({ className, tone, icon, children, ...props }: AlertProps) {
  return (
    <div role="status" className={cn(alertVariants({ tone }), className)} {...props}>
      {icon ? <span className="mt-0.5 shrink-0">{icon}</span> : null}
      <div className="flex flex-col gap-nc-1">{children}</div>
    </div>
  );
}

export function AlertTitle({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("font-nc-2", className)} {...props} />;
}

export function AlertDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-nc-2 leading-nc-2 text-muted-foreground", className)} {...props} />
  );
}
